import { useEffect, useRef, useState } from 'react'
import { Captions, Download, FileAudio, FileText, FolderOpen, Info, Languages, Video, X } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { SenderLogo } from '../../components/SenderLogo'
import { ToggleSwitch } from '../../components/ToggleSwitch'
import { DownloadProfileField } from '../../components/DownloadProfileField'
import { api, type DownloadJob, type DownloadProfile, type DownloadTargetPreview, type Film, type MediaRoot, type Quality } from '../../lib/api'
import { selectedSourceSummary, sourceForQuality, sourceResolution } from '../../lib/media'

interface Props {
  film: Film
  mediaRoots: MediaRoot[]
  onClose: () => void
}

export function DownloadDialog({ film, mediaRoots, onClose }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const profiles = useQuery({ queryKey: ['download-profiles'], queryFn: () => api<DownloadProfile[]>('/api/v1/download-profiles') })
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api<Record<string, unknown>>('/api/v1/settings') })
  const profileDefaultApplied = useRef(false)
  const [quality, setQuality] = useState<Quality>('HIGH')
  const [profileId, setProfileId] = useState('')
  const [root, setRoot] = useState(mediaRoots[0]?.id ?? 'downloads')
  const [subpath, setSubpath] = useState('')
  const [startAutomatically, setStartAutomatically] = useState(true)
  const [subtitles, setSubtitles] = useState(film.subtitles)
  const [info, setInfo] = useState(true)
  const [audioVariantFilmIds, setAudioVariantFilmIds] = useState<string[]>([])
  const applyProfile = (profile: DownloadProfile) => {
    setProfileId(profile.id); setQuality(profile.quality); setRoot(profile.mediaRootId)
    setSubtitles(film.subtitles && profile.subtitlesEnabled); setInfo(profile.infoFileEnabled)
  }
  const activeProfiles = profiles.data?.filter((item) => item.active) ?? []
  const selectedProfile = activeProfiles.find((item) => item.id === profileId)
  useEffect(() => {
    if (profileDefaultApplied.current || !profiles.data || !settings.data) return
    profileDefaultApplied.current = true
    const defaultProfileId = String(settings.data['download.defaultProfileId'] ?? '')
    const profile = profiles.data.find((item) => item.active && item.id === defaultProfileId)
      ?? profiles.data.find((item) => item.active)
    if (profile) applyProfile(profile)
    else setQuality((settings.data['download.defaultQuality'] as Quality | undefined) ?? 'HIGH')
  }, [profiles.data, settings.data])
  const debouncedSubpath = useDebouncedValue(subpath, 250)
  const preview = useQuery({
    queryKey: ['download-target-preview', film.id, profileId, quality, root, debouncedSubpath, subtitles, info, audioVariantFilmIds],
    queryFn: () => api<DownloadTargetPreview>('/api/v1/downloads/preview', {
      method: 'POST',
      body: JSON.stringify({
        filmId: film.id,
        downloadProfileId: profileId,
        quality,
        mediaRootId: root,
        destinationSubpath: debouncedSubpath,
        startAutomatically,
        subtitlesEnabled: subtitles,
        infoFileEnabled: info,
        audioVariantFilmIds,
      }),
    }),
    enabled: Boolean(selectedProfile && root),
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  })
  const mutation = useMutation({
    mutationFn: () => api<DownloadJob>('/api/v1/downloads', {
      method: 'POST',
      body: JSON.stringify({
        filmId: film.id,
        downloadProfileId: profileId,
        quality,
        mediaRootId: root,
        destinationSubpath: subpath,
        startAutomatically,
        subtitlesEnabled: subtitles,
        infoFileEnabled: info,
        audioVariantFilmIds,
      }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['downloads'] })
      onClose()
    },
  })

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <section className="modal-card download-modal" onMouseDown={(event) => event.stopPropagation()}>
        <header className="modal-header">
          <div><span className="eyebrow"><SenderLogo sender={film.sender} compact /></span><h2>{t('downloadDialog.title')}</h2></div>
          <button className="icon-button" onClick={onClose}><X /></button>
        </header>
        <div className="selected-film"><strong>{film.title}</strong><span>{film.topic}</span></div>
        <div className="form-grid">
          <DownloadProfileField className="full" profiles={profiles.data ?? []} roots={mediaRoots} value={profileId} loading={profiles.isLoading} onSelect={(profile) => { profileDefaultApplied.current = true; applyProfile(profile) }} />
          <label className="field full"><span>{t('downloadDialog.quality')}</span>
            <div className="segmented-control">
              {(['HIGH', 'NORMAL', 'LOW'] as Quality[]).map((value) => (
                <button key={value} disabled className={quality === value ? 'active' : ''} onClick={() => setQuality(value)}>
                  <strong>{t(`downloadDialog.${value.toLowerCase()}`)}</strong>
                  <small>{qualityHint(film, value, t)}</small>
                </button>
              ))}
            </div>
          </label>
          <div className="quality-explanation full"><Info /><div><strong>{t('downloadDialog.selectedSource')}</strong><span>{selectedSourceSummary(film, quality, t('downloadDialog.resolutionUnknown'))}</span>{film.sizeMb != null && <small>{t('downloadDialog.catalogSize', { size: film.sizeMb.toLocaleString('de-DE') })}</small>}</div></div>
          <label className="field"><span>{t('downloadDialog.root')}</span>
            <select value={root} disabled onChange={(event) => setRoot(event.target.value)}>
              {mediaRoots.map((item) => <option value={item.id} key={item.id}>{item.label}</option>)}
            </select>
          </label>
          <label className="field"><span>{t('downloadDialog.subpath')}</span>
            <div className="input-with-icon"><FolderOpen /><input value={subpath} onChange={(event) => setSubpath(event.target.value)} placeholder={t('downloadDialog.subpathHint')} /></div>
          </label>
        </div>
        <div className="option-list">
          <ToggleSwitch checked={startAutomatically} onChange={setStartAutomatically} label={t('downloadDialog.startNow')} />
          <ToggleSwitch checked={subtitles} onChange={setSubtitles} label={t('downloadDialog.subtitles')} disabled hint={!film.subtitles ? t('downloadDialog.noSubtitles') : undefined} />
          <ToggleSwitch checked={info} onChange={setInfo} label={t('downloadDialog.info')} disabled />
        </div>
        {film.audioVariants.length > 0 && <section className="audio-variant-options">
          <header><Languages /><div><strong>{t('downloadDialog.additionalAudio')}</strong><small>{t('downloadDialog.additionalAudioHint')}</small></div></header>
          {film.audioVariants.map((variant) => <label key={variant.filmId}>
            <input type="checkbox" checked={audioVariantFilmIds.includes(variant.filmId)} onChange={(event) => setAudioVariantFilmIds((current) => event.target.checked ? [...current, variant.filmId] : current.filter((id) => id !== variant.filmId))} />
            <span><strong>{variant.label}</strong><small>{variant.languageCode ? variant.languageCode.toUpperCase() : t('downloadDialog.originalLanguageUnknown')}{variant.sizeMb != null ? ` · ${variant.sizeMb.toLocaleString('de-DE')} MB ${t('downloadDialog.temporarySource')}` : ''}</small></span>
          </label>)}
        </section>}
        <DownloadTargetSummary
          preview={preview.data}
          loading={preview.isLoading}
          updating={subpath !== debouncedSubpath || preview.isFetching}
          error={preview.error instanceof Error ? preview.error.message : undefined}
          t={t}
        />
        {mutation.error && <div className="inline-error">{mutation.error.message}</div>}
        <footer className="modal-actions">
          <button className="quiet-button" onClick={onClose}>{t('common.cancel')}</button>
          <button className="primary-button" disabled={mutation.isPending || preview.isError || !root || !selectedProfile} onClick={() => mutation.mutate()}>
            <Download size={17} /> {t('downloadDialog.submit')}
          </button>
        </footer>
      </section>
    </div>
  )
}

export function BulkDownloadDialog({ films, mediaRoots, onClose, onCompleted }: { films: Film[]; mediaRoots: MediaRoot[]; onClose: () => void; onCompleted: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const profiles = useQuery({ queryKey: ['download-profiles'], queryFn: () => api<DownloadProfile[]>('/api/v1/download-profiles') })
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api<Record<string, unknown>>('/api/v1/settings') })
  const [profileId, setProfileId] = useState('')
  const [quality, setQuality] = useState<Quality>('HIGH')
  const [root, setRoot] = useState(mediaRoots[0]?.id ?? 'downloads')
  const [subpath, setSubpath] = useState('')
  const [startAutomatically, setStartAutomatically] = useState(true)
  const [subtitles, setSubtitles] = useState(true)
  const [info, setInfo] = useState(true)
  const profileDefaultApplied = useRef(false)
  const activeProfiles = profiles.data?.filter((profile) => profile.active) ?? []
  const selectedProfile = activeProfiles.find((profile) => profile.id === profileId)
  const applyProfile = (profile: DownloadProfile) => {
    setProfileId(profile.id); setQuality(profile.quality); setRoot(profile.mediaRootId)
    setSubtitles(profile.subtitlesEnabled); setInfo(profile.infoFileEnabled)
  }
  useEffect(() => {
    if (profileDefaultApplied.current || !profiles.data || !settings.data) return
    profileDefaultApplied.current = true
    const defaultProfileId = String(settings.data['download.defaultProfileId'] ?? '')
    const profile = profiles.data.find((item) => item.active && item.id === defaultProfileId)
      ?? profiles.data.find((item) => item.active)
    if (profile) applyProfile(profile)
    else setQuality((settings.data['download.defaultQuality'] as Quality | undefined) ?? 'HIGH')
  }, [profiles.data, settings.data])
  const mutation = useMutation({ mutationFn: () => api<{ created: DownloadJob[]; skipped: Record<string, string> }>('/api/v1/downloads/bulk', { method: 'POST', body: JSON.stringify({ filmIds: films.map((film) => film.id), downloadProfileId: profileId, quality, mediaRootId: root, destinationSubpath: subpath, startAutomatically, subtitlesEnabled: subtitles, infoFileEnabled: info }) }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['downloads'] }); onCompleted(); onClose() } })
  return <div className="modal-backdrop" onMouseDown={onClose}><section className="modal-card" onMouseDown={(event) => event.stopPropagation()}><header className="modal-header"><div><span className="eyebrow"><Download />{films.length}</span><h2>{t('downloadDialog.bulkTitle')}</h2></div><button className="icon-button" onClick={onClose}><X /></button></header><p className="bulk-download-summary">{t('downloadDialog.bulkSummary', { count: films.length })}</p><div className="form-grid">
    <DownloadProfileField className="full" profiles={profiles.data ?? []} roots={mediaRoots} value={profileId} loading={profiles.isLoading} onSelect={(profile) => { profileDefaultApplied.current = true; applyProfile(profile) }} />
    <label className="field full"><span>{t('downloadDialog.subpath')}</span><input value={subpath} onChange={(event) => setSubpath(event.target.value)} /></label>
  </div><div className="option-list"><ToggleSwitch checked={startAutomatically} onChange={setStartAutomatically} label={t('downloadDialog.startNow')} /><ToggleSwitch checked={subtitles} onChange={setSubtitles} label={t('downloadDialog.subtitles')} disabled /><ToggleSwitch checked={info} onChange={setInfo} label={t('downloadDialog.info')} disabled /></div>{mutation.error && <div className="inline-error">{mutation.error.message}</div>}<footer className="modal-actions"><button className="quiet-button" onClick={onClose}>{t('common.cancel')}</button><button className="primary-button" disabled={mutation.isPending || !root || !selectedProfile} onClick={() => mutation.mutate()}><Download />{t('downloadDialog.bulkSubmit', { count: films.length })}</button></footer></section></div>
}

function qualityHint(film: Film, quality: Quality, t: (key: string) => string) {
  const direct = quality === 'HIGH' ? film.highUrl : quality === 'LOW' ? film.lowUrl : film.normalUrl
  if (!direct) return t('downloadDialog.fallbackNormal')
  return sourceResolution(sourceForQuality(film, quality)) ?? t('downloadDialog.resolutionUnknown')
}

function DownloadTargetSummary({ preview, loading, updating, error, t }: {
  preview?: DownloadTargetPreview
  loading: boolean
  updating: boolean
  error?: string
  t: (key: string) => string
}) {
  if (loading && !preview) return <div className="download-target-preview pending"><FolderOpen /><span>{t('downloadDialog.targetLoading')}</span></div>
  if (error && !preview) return <div className="download-target-preview failed"><Info /><span>{error}</span></div>
  if (!preview) return null
  return <section className="download-target-preview">
    <header><FolderOpen /><div><strong>{t('downloadDialog.targetFolder')}</strong><code>{preview.containerDirectory}</code></div>{updating && <small>{t('downloadDialog.targetUpdating')}</small>}</header>
    <div className="download-target-files">
      {preview.files.map((file) => {
        const Icon = file.type === 'VIDEO' ? Video : file.type === 'AUDIO' ? FileAudio : file.type === 'SUBTITLE' ? Captions : FileText
        const pendingSubtitleFormat = file.type === 'SUBTITLE' && file.alternatives
        const filenames = pendingSubtitleFormat ? file.filenames.slice(0, 1).map(withoutSubtitleExtension) : file.filenames
        const containerPaths = pendingSubtitleFormat ? file.containerPaths.slice(0, 1).map(withoutSubtitleExtension) : file.containerPaths
        return <div className="download-target-file" key={file.type}>
          <Icon />
          <div>
            <span>{t(`downloadDialog.target${file.type === 'VIDEO' ? 'Video' : file.type === 'AUDIO' ? 'Audio' : file.type === 'SUBTITLE' ? 'Subtitle' : 'Info'}`)}{pendingSubtitleFormat ? ` · ${t('downloadDialog.targetExtensionPending')}` : ''}</span>
            <code>{filenames.join(', ')}</code>
            <small>{containerPaths.join(', ')}</small>
          </div>
        </div>
      })}
    </div>
    {preview.files.some((file) => file.type === 'SUBTITLE' && file.alternatives) && <p>{t('downloadDialog.subtitleFormatHint')}</p>}
  </section>
}

function withoutSubtitleExtension(path: string) {
  return path.replace(/\.(?:vtt|ttml)$/i, '')
}

function useDebouncedValue<T>(value: T, delay: number) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timeout)
  }, [value, delay])
  return debounced
}
